import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Label from '../../../components/form/Label'
import { RichTextEditor } from '../../../components/global/form/RichTextEditor'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import { StyledButtonLink } from '../../../components/table/Td'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyCoachToken } from '../../../hooks/useVerifyCoachToken'
import { apiClient } from '../../../services/apiClient'
import { SkillStatusEnum, UserSkill } from '../../../types/skill.type'
import { UserModel } from '../../../types/user.type'
import { StyledButton } from '../../admin/Login'
import { AdminContainer, TitleWrapper } from '../../admin/users/AdminUsers'
import { AdminForm } from '../../admin/users/AdminUsersForm'

const CoachMyMembersSingleMemberSkillsForm = () => {
    const user = useContext(UserContext)
    const { t } = useTranslation('translation')

    const { memberId, skillId } = useParams()

    const verifyToken = useVerifyCoachToken()

    const [comment, setComment] = useState<string>()
    const [status, setStatus] = useState<SkillStatusEnum | undefined>()

    const lng = useLangSelected()

    const navigate = useNavigate()
    const BACK_LINK = `/${lng}/bva-coach/mes-membres/${memberId}`

    const { data: member } = useQuery({
        queryKey: ['coach-member', memberId],
        queryFn: async () => {
            verifyToken()
            const response = await apiClient.get<UserModel>(`/users/${memberId}`)
            return response.data
        },
        enabled: !!memberId && !!user,
    })

    const { data: skillUser } = useQuery({
        queryKey: ['skillUser', skillId],
        queryFn: async () => {
            verifyToken()
            const response = await apiClient.get<UserSkill>(`/skill-users/${skillId}`)
            return response.data
        },
        enabled: !!skillId && !!user,
    })

    const { mutate: updateSkill } = useMutation({
        mutationFn: (data: Partial<UserSkill>) =>
            apiClient.patch(`/skill-users/${skillId}`, data).then(() => {
                toast.success(`La compétence ${skillUser?.skill.name} de ${member?.firstName} a bien été mis à jour.`)
                navigate(BACK_LINK)
            }),
    })

    const submit = useCallback(async () => {
        verifyToken()

        updateSkill({
            comment,
            status,
        })
    }, [comment, status])

    useEffect(() => {
        if (skillUser) {
            setComment(skillUser.comment)
            setStatus(skillUser.status)
        }
    }, [skillUser])

    if (!member || !skillUser) return <Loader />

    return (
        <div>
            <TitleWrapper>
                <Title>
                    {member.firstName} {member.lastName} - {skillUser.skill.name}
                </Title>

                <StyledButtonLink to={`/${lng}/bva-coach/mes-membres/${memberId}`} className="btn btn-primary mb-2">
                    {t('global.back')}
                </StyledButtonLink>
            </TitleWrapper>
            <AdminContainer>
                <AdminForm>
                    <Form.Group className="mb-3">
                        <Label>Commentaire</Label>
                        <RichTextEditor
                            value={comment ?? skillUser.comment ?? ''}
                            onChange={setComment}
                            className="mb-4"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Statut *</Label>
                        <Form.Select
                            onChange={(e) => setStatus(e.target.value as SkillStatusEnum)}
                            value={status ?? skillUser.status ?? 'placeholder'}
                        >
                            <option value={'placeholder'} disabled>
                                Sélectionnez un statut
                            </option>
                            <option value={SkillStatusEnum.TO_DISCOVER}>À découvrir</option>
                            <option value={SkillStatusEnum.STARTED}>Commencé</option>
                            <option value={SkillStatusEnum.CONFIRMED}>Confirmé</option>
                            <option value={SkillStatusEnum.MASTERED}>Maîtrisé</option>
                        </Form.Select>
                    </Form.Group>
                    <StyledButton variant="primary" type="button" onClick={submit}>
                        Modifier
                    </StyledButton>
                </AdminForm>
            </AdminContainer>
        </div>
    )
}

export default CoachMyMembersSingleMemberSkillsForm
